import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useApolloClient } from "@apollo/client/react";
import { parseCookies } from "nookies";
import Link from "next/link";

import { useUserInfo } from "hocs/UserInfoContext";
import { useSidebar } from "hocs/SidebarContext";
import { generateNavList } from "helpers/navList";
// Nav items prefetch on hover, not on sight: the Reports route pulls in
// @react-pdf/renderer, and the sidebar is on every page — so viewport
// prefetching was downloading and evaluating ~1.2 MB of PDF engine on every
// screen in the app, blocking the main thread while people tried to click.
import IntentLink from "components/IntentLink/IntentLink";
import { signOut } from "aws-amplify/auth";
import { destroyCognitoCookies } from "helpers/index";
import ActAsMenu from "components/ActAs/ActAsMenu";

import {
  ClipboardDocumentListIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  BriefcaseIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

/**
 * Icon lookup keyed by nav item name.
 * Falls back to ClipboardDocumentListIcon for unknown items.
 */
const NAV_ICONS = {
  Valuations: ClipboardDocumentListIcon,
  Banks: BuildingLibraryIcon,
  Organisations: UserGroupIcon,
  Companies: BriefcaseIcon,
  Reports: ChartBarIcon,
  Settings: Cog6ToothIcon,
  "Panel of Valuers": UsersIcon,
};

const getInitials = (firstName, lastName) => {
  const first = firstName?.charAt(0) || "";
  const last = lastName?.charAt(0) || "";
  return (first + last).toUpperCase();
};

const formatRole = (role) => {
  if (!role) return "";
  return role
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
};

/** Sidebar panel toggle icon — rectangle with vertical divider */
const PanelIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2.5" y="3" width="15" height="14" rx="2.5" />
    <line x1="7.5" y1="3" x2="7.5" y2="17" />
  </svg>
);

/**
 * Desktop-only left sidebar — Claude/Linear-inspired with collapse toggle.
 * Hidden on mobile (< lg), where bottom tab nav is used instead.
 * Collapsed state shows icons only; expanded shows full labels.
 * Avatar at bottom opens a popout menu (Settings + Sign out).
 */
const DesktopSidebar = () => {
  const { currentUser, canActAs } = useUserInfo();
  const router = useRouter();
  const client = useApolloClient();
  const { collapsed, toggleSidebar } = useSidebar();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const menuRef = useRef(null);
  const avatarRef = useRef(null);

  const navList = generateNavList(currentUser?.role);

  // Close popout on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close popout on route change
  useEffect(() => {
    const handleRoute = () => setIsMenuOpen(false);
    router.events.on("routeChangeStart", handleRoute);
    return () => router.events.off("routeChangeStart", handleRoute);
  }, [router]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    const cookies = parseCookies();
    destroyCognitoCookies(cookies);
    try {
      await client.resetStore();
    } catch (_) {
      // Ignore — sign out should still proceed
    }
    try {
      await signOut();
    } catch (_) {
      // Token revocation may fail on network errors — proceed anyway
    }
    location.reload();
  };

  /**
   * Match logic mirrors NavigationLink: checks exact path OR
   * the displayAsSelected prefix for grouped routes like /profile/*.
   */
  const isActive = (item) => {
    const path = router.asPath;
    if (path === item.url) return true;
    if (item.displayAsSelected && path.startsWith(item.displayAsSelected))
      return true;
    if (
      !item.displayAsSelected &&
      path.startsWith(item.url) &&
      item.url !== "/"
    )
      return true;
    return false;
  };

  const initials = getInitials(currentUser?.firstName, currentUser?.lastName);
  const fullName = [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <aside
      className={`hidden lg:flex lg:flex-col lg:shrink-0 bg-card border-r border-border/50 transition-[width] duration-200 ease-in-out ${
        collapsed ? "lg:w-[60px]" : "lg:w-52"
      }`}
    >
      {/* ── Logo / Toggle ── */}
      {/* Asymmetric padding: left padding anchors the logo to the same
          14px gutter as the nav items below; right padding is tightened
          so the collapse toggle sits closer to the sidebar's right edge. */}
      <div className="flex items-center shrink-0 h-11 pl-3.5 pr-1.5">
        <Link href="/valuation/list" legacyBehavior>
          <a
            className={`min-w-0 overflow-hidden transition-all duration-200 ${
              collapsed
                ? "w-0 max-w-0 opacity-0"
                : "flex-1 opacity-100"
            }`}
          >
            <img
              src="/image/logo.svg"
              alt="Central Valuations"
              draggable="false"
              // brightness-0 filter renders any non-transparent pixel as
              // pure black — flattens the blue tick + black outline into a
              // single-colour monochrome mark.
              className="h-6 brightness-0 dark:invert"
            />
          </a>
        </Link>
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-150 shrink-0"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <PanelIcon
            className={`h-4 w-4 transition-transform duration-200 ${
              collapsed ? "scale-x-[-1]" : ""
            }`}
          />
        </button>
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 space-y-px overflow-y-auto overflow-x-hidden px-3.5">
        {navList.map((item) => {
          const Icon = NAV_ICONS[item.name] || ClipboardDocumentListIcon;
          const active = isActive(item);

          return (
            <IntentLink href={item.url} key={item.url} legacyBehavior>
              <a
                title={collapsed ? item.name : undefined}
                className={`group flex items-center px-2 py-1.5 rounded-md text-nav whitespace-nowrap transition-colors duration-150 ${
                  active
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span
                  className={`truncate overflow-hidden transition-all duration-200 ${
                    collapsed
                      ? "max-w-0 opacity-0 ml-0"
                      : "max-w-[200px] opacity-100 ml-2"
                  }`}
                >
                  {item.name}
                </span>
              </a>
            </IntentLink>
          );
        })}
      </nav>

      {/* ── User section with popout menu ── */}
      <div className="relative border-t border-border/50 py-2.5 px-2">
        {/* Avatar button — opens popout */}
        <button
          ref={avatarRef}
          onClick={() => setIsMenuOpen((prev) => !prev)}
          className="flex items-center justify-center w-full py-1.5 rounded-md transition-colors duration-150 hover:bg-accent"
        >
          <div className="h-7 w-7 rounded-full bg-foreground text-background flex items-center justify-center text-[10px] font-semibold shrink-0">
            {initials}
          </div>
          <div
            className={`min-w-0 flex-1 overflow-hidden text-left transition-all duration-200 ${
              collapsed ? "max-w-0 opacity-0 ml-0" : "opacity-100 ml-2"
            }`}
          >
            <p className="text-nav font-medium text-foreground truncate leading-tight">
              {fullName}
            </p>
            <p className="text-[11px] text-muted-foreground truncate leading-tight">
              {formatRole(currentUser?.role)}
            </p>
          </div>
        </button>

        {/* Popout menu */}
        <div
          ref={menuRef}
          className={`absolute z-50 ${canActAs ? "w-80" : "w-56"} rounded-md bg-card shadow-lg border border-border ${
            collapsed
              ? "left-full bottom-0 ml-2"
              : "bottom-full left-2 right-2 mb-2 w-auto"
          } ${
            isMenuOpen
              ? "opacity-100 scale-100"
              : "opacity-0 scale-95 pointer-events-none"
          }`}
          aria-hidden={!isMenuOpen}
          tabIndex={isMenuOpen ? undefined : -1}
          style={{
            transitionProperty: "opacity, transform",
            transitionDuration: "var(--cv-transition-normal)",
            transitionTimingFunction: "var(--cv-ease-out)",
          }}
        >
          <div className="p-1">
            {/* User info header */}
            <div className="px-3 py-2.5 border-b border-border mb-1">
              <p className="text-sm font-medium text-foreground truncate">
                {fullName}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {formatRole(currentUser?.role)}
              </p>
              {currentUser?.businessSubjectName && (
                <p className="text-xs text-muted-foreground truncate">
                  {currentUser.businessSubjectName}
                </p>
              )}
            </div>

            {/* Settings link */}
            <IntentLink href="/profile/settings" legacyBehavior>
              <a
                onClick={() => setIsMenuOpen(false)}
                className="block px-3 py-2 text-sm text-foreground hover:bg-accent rounded transition-colors duration-150"
              >
                Settings
              </a>
            </IntentLink>

            {/* Sign out */}
            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className="flex items-center text-left w-full px-3 py-2 text-sm text-foreground hover:bg-accent rounded transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSigningOut ? "Signing out\u2026" : "Sign out"}
            </button>

            {/* Super admins: act as a bank / company / organisation user */}
            <ActAsMenu />
          </div>
        </div>
      </div>
    </aside>
  );
};

export default DesktopSidebar;
